/* seo.js — per-route SEO manager (browser side).
   Updates <head> on every navigation / language switch: title, meta description,
   canonical, hreflang alternates, Open Graph / Twitter tags, robots and per-page
   JSON-LD. All pure logic lives in seo-core.js (shared with the build-time
   prerenderer, scripts/prerender-heads.mjs, which bakes the same tags into static
   HTML so crawlers see them without executing JS). Runtime values match the
   prerendered ones — consistent raw-vs-rendered signals, which Google requires. */

import { tr } from "./i18n.js";
import { ORIGIN, LANGS, INDEX_LANGS, OG_LOCALE, resolveMeta, buildJsonLd, pathFor, mktCat, pageIndexLangs } from "./seo-core.js";

function setMetaBy(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
const setMeta = (name, c) => setMetaBy("name", name, c);
const setProp = (prop, c) => setMetaBy("property", prop, c);

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

function setHreflang(page, param) {
  document.head.querySelectorAll("link[data-seo-hreflang]").forEach(el => el.remove());
  const langs = pageIndexLangs(page, param, tr);   // only languages this page is actually translated in
  if (langs.length <= 1) return;                   // EN-only page → no alternates
  const add = (hreflang, href) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    el.setAttribute("data-seo-hreflang", "1");
    document.head.appendChild(el);
  };
  langs.forEach(l => add(l, ORIGIN + pathFor(page, param, l)));
  add("x-default", ORIGIN + pathFor(page, param, "en"));
}

function setJsonLd(blocks) {
  let el = document.getElementById("seo-ld-page");
  if (!blocks || !blocks.length) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "seo-ld-page";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(blocks.length === 1 ? blocks[0] : blocks);
}

export function applySeo(page, param, lang) {
  if (typeof document === "undefined") return;
  const meta = resolveMeta(page, param, lang, tr);
  const known = !!meta;
  const m = meta || resolveMeta("home", null, lang, tr);

  /* Marketplace category pages (/marketplace/crypto…) self-canonicalize (normalised to the
     lowercase slug); other filtered marketplace views are duplicates of /marketplace; unknown
     routes canonicalize home. */
  const canonPage = known ? page : "home";
  const canonParam = page === "marketplace" ? (mktCat(param) ? String(param).toLowerCase() : null) : param;
  const url = ORIGIN + pathFor(canonPage, known ? canonParam : null, lang);

  document.title = m.t;
  setMeta("description", m.d);
  setMeta("robots", (known && pageIndexLangs(canonPage, known ? canonParam : null, tr).includes(lang)) ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" : "noindex, follow");
  setCanonical(url);
  setHreflang(canonPage, known ? canonParam : null);

  setProp("og:type", m.ogType || "website");
  setProp("og:url", url);
  setProp("og:title", m.t);
  setProp("og:description", m.d);
  setProp("og:locale", OG_LOCALE[lang] || "en_US");
  if (m.img) { setProp("og:image", m.img); setMeta("twitter:image", m.img); }
  else {
    setProp("og:image", ORIGIN + "/og-image.png?v=4");
    setMeta("twitter:image", ORIGIN + "/og-image.png?v=4");
  }
  setProp("article:published_time", m.ogType === "article" && m.date ? m.date : "");
  setMeta("twitter:title", m.t);
  setMeta("twitter:description", m.d);

  setJsonLd(known ? buildJsonLd(page, param, m.t, lang, tr) : []);
}
